import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { useAuth } from "../contexts/AuthContext";
import { apiService } from "../services/apiService";

const SCANNER_REGION_ID = "tv-connect-qr-reader";

const TvConnect = () => {
    const { user, isAuthenticated } = useAuth();

    // Form state
    const [pairCode, setPairCode] = useState("");
    const [selectedTableId, setSelectedTableId] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Data state
    const [tables, setTables] = useState([]);
    const [devices, setDevices] = useState([]);
    const [loadingTables, setLoadingTables] = useState(true);
    const [loadingDevices, setLoadingDevices] = useState(true);
    const [unlinkingId, setUnlinkingId] = useState(null);

    // Scanner state
    const [scannerOpen, setScannerOpen] = useState(false);
    const [scannerError, setScannerError] = useState(null);
    const scannerRef = useRef(null);

    useEffect(() => {
        if (!user?.id) return;
        fetchTables();
        fetchDevices();
    }, [user?.id]);

    // Auto-fill the pair code from ?code=XXXX-XXXX URL param.
    // This is what the TV's QR code links to, so scanning it with a phone
    // camera (outside the panel) lands here with the code already filled.
    useEffect(() => {
        try {
            const params = new URLSearchParams(window.location.search);
            const codeParam = params.get("code");
            if (codeParam) {
                const raw = codeParam.replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 8);
                if (raw.length === 8) {
                    setPairCode(`${raw.slice(0, 4)}-${raw.slice(4)}`);
                }
            }
        } catch (e) {
            // ignore
        }
    }, []);

    // Tear the camera down when the modal closes or the component unmounts.
    useEffect(() => {
        return () => {
            stopScanner();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchTables = async () => {
        try {
            setLoadingTables(true);
            const data = await apiService.post("/club/tables", { id: user.id });
            setTables(data.tables || data || []);
        } catch (err) {
            console.error("Error fetching tables:", err);
        } finally {
            setLoadingTables(false);
        }
    };

    const fetchDevices = async () => {
        try {
            setLoadingDevices(true);
            const data = await apiService.get("/devices");
            setDevices(data.devices || []);
        } catch (err) {
            console.error("Error fetching devices:", err);
        } finally {
            setLoadingDevices(false);
        }
    };

    /**
     * Extract a pair code from a scanned QR payload.
     * The TV generates QRs pointing at `${WEB_PANEL_URL}/tv-connect?code=ABCD-1234`.
     * Accept either the full URL or just the bare code.
     */
    const extractPairCode = (raw) => {
        if (!raw) return null;
        let candidate = raw.trim();
        try {
            if (candidate.includes("://") || candidate.startsWith("/")) {
                const url = new URL(candidate, window.location.origin);
                const fromQuery = url.searchParams.get("code");
                if (fromQuery) candidate = fromQuery;
            }
        } catch (e) {
            // not a URL, use as-is
        }
        const cleaned = candidate.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
        if (cleaned.length !== 8) return null;
        return `${cleaned.slice(0, 4)}-${cleaned.slice(4)}`;
    };

    const stopScanner = async () => {
        const instance = scannerRef.current;
        if (!instance) return;
        try {
            if (typeof instance.isScanning === "boolean" ? instance.isScanning : true) {
                await instance.stop();
            }
            await instance.clear();
        } catch (e) {
            // ignore — happens when already stopped
        } finally {
            scannerRef.current = null;
        }
    };

    const openScanner = async () => {
        setScannerError(null);
        setScannerOpen(true);
        // Wait for the modal DOM node to mount before initializing the camera
        setTimeout(async () => {
            try {
                if (!window.isSecureContext) {
                    setScannerError(
                        "Camera access requires HTTPS. This site is served over plain HTTP, " +
                        "so your browser will block the camera. Ask your admin to enable SSL, " +
                        "or type the code manually."
                    );
                    return;
                }
                if (!navigator.mediaDevices?.getUserMedia) {
                    setScannerError("Your browser does not support camera access.");
                    return;
                }

                const instance = new Html5Qrcode(SCANNER_REGION_ID, { verbose: false });
                scannerRef.current = instance;

                await instance.start(
                    { facingMode: "environment" },
                    {
                        fps: 10,
                        qrbox: { width: 260, height: 260 },
                        aspectRatio: 1.0,
                    },
                    async (decodedText) => {
                        const code = extractPairCode(decodedText);
                        if (!code) return; // ignore unrelated QR codes
                        setPairCode(code);
                        setError(null);
                        setSuccess(null);
                        await stopScanner();
                        setScannerOpen(false);
                    },
                    () => {
                        // scan frame with no QR — ignore silently
                    },
                );
            } catch (e) {
                const msg = e?.message || String(e);
                if (/permission|denied|NotAllowed/i.test(msg)) {
                    setScannerError("Camera permission denied. Please allow camera access and try again.");
                } else if (/NotFound|no camera/i.test(msg)) {
                    setScannerError("No camera was found on this device.");
                } else {
                    setScannerError("Could not start the camera: " + msg);
                }
            }
        }, 50);
    };

    const closeScanner = async () => {
        await stopScanner();
        setScannerError(null);
        setScannerOpen(false);
    };

    const handlePairCodeChange = (e) => {
        // Keep only alphanumerics, uppercase, auto-insert hyphen after 4 chars
        const raw = e.target.value.replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 8);
        const formatted = raw.length > 4 ? `${raw.slice(0, 4)}-${raw.slice(4)}` : raw;
        setPairCode(formatted);
        setError(null);
        setSuccess(null);
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        setError(null);
        setSuccess(null);

        if (!/^[A-Z0-9]{4}-[0-9]{4}$/.test(pairCode)) {
            setError("Pair code must be in the format ABCD-1234");
            return;
        }
        if (!selectedTableId) {
            setError("Please select a table to assign");
            return;
        }

        setSubmitting(true);
        try {
            await apiService.post("/device/pair/link", {
                pair_code: pairCode,
                table_id: parseInt(selectedTableId, 10),
            });
            setSuccess("TV linked successfully! The TV will update in a few seconds.");
            setPairCode("");
            setSelectedTableId("");
            // Refresh devices list
            setTimeout(fetchDevices, 1500);
        } catch (err) {
            setError(err.message || "Failed to link device");
        } finally {
            setSubmitting(false);
        }
    };

    const handleUnlink = async (deviceId) => {
        if (!window.confirm("Unlink this TV? It will need to be paired again.")) return;
        setUnlinkingId(deviceId);
        try {
            await apiService.delete(`/device/${deviceId}`);
            await fetchDevices();
        } catch (err) {
            alert(err.message || "Failed to unlink device");
        } finally {
            setUnlinkingId(null);
        }
    };

    const formatDate = (date) => {
        if (!date) return "Never";
        try {
            return new Date(date).toLocaleString();
        } catch {
            return "Unknown";
        }
    };

    if (!isAuthenticated()) return null;

    return (
        <div className="page-wrapper">
            <div className="page-header d-print-none">
                <div className="container-xl">
                    <div className="row g-2 align-items-center">
                        <div className="col">
                            <div className="page-pretitle text-primary">Device Management</div>
                            <h2 className="page-title">TV Connect</h2>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-body">
                <div className="container-xl">
                    <div className="row row-deck row-cards">
                        {/* Pairing Form Card */}
                        <div className="col-lg-5">
                            <div className="card">
                                <div className="card-header">
                                    <h3 className="card-title">Link a New TV</h3>
                                </div>
                                <div className="card-body">
                                    <p className="text-secondary mb-3">
                                        Open the app on your TV, enter the 8-character code shown on
                                        the screen, and select which table to assign it to.
                                    </p>

                                    {success && (
                                        <div className="alert alert-success" role="alert">
                                            {success}
                                        </div>
                                    )}
                                    {error && (
                                        <div className="alert alert-danger" role="alert">
                                            {error}
                                        </div>
                                    )}

                                    <form onSubmit={handleSubmit}>
                                        <div className="mb-3">
                                            <label className="form-label d-flex justify-content-between align-items-center">
                                                <span>Pair Code</span>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={openScanner}
                                                    disabled={submitting}
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6, verticalAlign: "-3px" }}>
                                                        <path d="M3 7V5a2 2 0 0 1 2 -2h2" />
                                                        <path d="M17 3h2a2 2 0 0 1 2 2v2" />
                                                        <path d="M21 17v2a2 2 0 0 1 -2 2h-2" />
                                                        <path d="M7 21h-2a2 2 0 0 1 -2 -2v-2" />
                                                        <rect x="8" y="8" width="8" height="8" rx="1" />
                                                    </svg>
                                                    Scan QR
                                                </button>
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control form-control-lg text-center"
                                                style={{
                                                    fontFamily: "monospace",
                                                    fontSize: "1.5rem",
                                                    letterSpacing: "0.1rem",
                                                }}
                                                placeholder="ABCD-1234"
                                                value={pairCode}
                                                onChange={handlePairCodeChange}
                                                maxLength={9}
                                                disabled={submitting}
                                                autoComplete="off"
                                            />
                                            <small className="form-hint">
                                                Type the code shown on the TV, or tap <strong>Scan QR</strong> to point your camera at the TV.
                                            </small>
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label">Assign to Table</label>
                                            <select
                                                className="form-select"
                                                value={selectedTableId}
                                                onChange={(e) => setSelectedTableId(e.target.value)}
                                                disabled={submitting || loadingTables}
                                            >
                                                <option value="">Select a snooker table…</option>
                                                {tables
                                                    .filter((t) => !t.deleted_at && t.type === 'snooker')
                                                    .map((t) => (
                                                        <option key={t.id} value={t.id}>
                                                            {t.name}
                                                        </option>
                                                    ))}
                                            </select>
                                        </div>

                                        <button
                                            type="submit"
                                            className="btn btn-primary w-100"
                                            disabled={submitting || !pairCode || !selectedTableId}
                                        >
                                            {submitting ? "Linking…" : "Link TV"}
                                        </button>
                                    </form>
                                </div>
                            </div>
                        </div>

                        {/* Paired Devices Card */}
                        <div className="col-lg-7">
                            <div className="card">
                                <div className="card-header">
                                    <h3 className="card-title">Paired TVs</h3>
                                    <div className="card-actions">
                                        <button
                                            className="btn btn-sm btn-link"
                                            onClick={fetchDevices}
                                            disabled={loadingDevices}
                                        >
                                            {loadingDevices ? "Loading…" : "Refresh"}
                                        </button>
                                    </div>
                                </div>
                                <div className="card-body p-0">
                                    {loadingDevices ? (
                                        <div className="text-center py-5">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading…</span>
                                            </div>
                                        </div>
                                    ) : devices.length === 0 ? (
                                        <div className="empty">
                                            <p className="empty-title">No TVs paired yet</p>
                                            <p className="empty-subtitle text-secondary">
                                                Install the Snooker Scoreboard app on your TV and
                                                enter the pair code shown there.
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="table-responsive">
                                            <table className="table table-vcenter card-table">
                                                <thead>
                                                    <tr>
                                                        <th>Device</th>
                                                        <th>Table</th>
                                                        <th>Last Seen</th>
                                                        <th className="w-1"></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {devices.map((d) => (
                                                        <tr key={d.id}>
                                                            <td>
                                                                <div className="font-weight-medium">
                                                                    {d.name || `Device ${d.id}`}
                                                                </div>
                                                                <div className="text-secondary small">
                                                                    {d.platform || "Unknown platform"}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                {d.table?.name || (
                                                                    <span className="text-secondary">
                                                                        Not assigned
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td className="text-secondary">
                                                                {formatDate(d.last_seen_at)}
                                                            </td>
                                                            <td className="text-end">
                                                                <button
                                                                    className="btn btn-sm btn-danger"
                                                                    onClick={() => handleUnlink(d.id)}
                                                                    disabled={unlinkingId === d.id}
                                                                >
                                                                    {unlinkingId === d.id
                                                                        ? "…"
                                                                        : "Unlink"}
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* QR Scanner Modal */}
            {scannerOpen && (
                <div
                    className="modal d-block"
                    tabIndex="-1"
                    role="dialog"
                    style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
                    onClick={(e) => {
                        if (e.target === e.currentTarget) closeScanner();
                    }}
                >
                    <div className="modal-dialog modal-dialog-centered" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Scan TV QR Code</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    aria-label="Close"
                                    onClick={closeScanner}
                                />
                            </div>
                            <div className="modal-body">
                                {scannerError ? (
                                    <div className="alert alert-warning mb-0" role="alert">
                                        {scannerError}
                                    </div>
                                ) : (
                                    <>
                                        <p className="text-secondary mb-2">
                                            Point your camera at the QR code shown on the TV pairing screen.
                                        </p>
                                        <div
                                            id={SCANNER_REGION_ID}
                                            style={{
                                                width: "100%",
                                                maxWidth: 400,
                                                margin: "0 auto",
                                                borderRadius: 6,
                                                overflow: "hidden",
                                                background: "#000",
                                            }}
                                        />
                                    </>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-link"
                                    onClick={closeScanner}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TvConnect;
